/**
 * Block Analyzer — shared lot selection store.
 *
 * Manages a small set of selected BBLs that is synced between the
 * block-analyzer grid (checkboxes) and the embedded block map
 * (dot-pattern fill + click-to-toggle).
 *
 * The store is attached to the `#cm_block_analyzer_part` container element,
 * so both the grid (mounted via PsGrids) and the embedded map (mounted via
 * PsEmbeddedMaps) can reach it by walking up from their own DOM nodes.
 *
 * Lifecycle: a new partial mount produces a new container element, so a
 * fresh store is created each time the block-analyzer tab loads (e.g. when
 * the current lot changes). Old stores are garbage-collected with their
 * detached DOM.
 *
 * Exposed as `window.PsBlockAnalyzerSelection`.
 */

const CONTAINER_SELECTOR = '#cm_block_analyzer_part';
const STORE_PROP = '_psBlockAnalyzerSelection';

function findContainer(node) {
  if (!node) return null;
  if (node.closest) return node.closest(CONTAINER_SELECTOR);
  return null;
}

function createStore() {
  const selected = new Set();         // bbls as strings
  const allowed  = new Set();         // bbls allowed to be selected (current block, non-historic)
  const listeners = new Set();

  function notify() {
    const snapshot = Array.from(selected);
    listeners.forEach((fn) => {
      try { fn(snapshot); } catch (err) { console.error('[block-analyzer-selection] listener error', err); }
    });
  }

  return {
    /* ----- allowed set: only these BBLs can be selected ----- */
    setAllowed(bbls) {
      allowed.clear();
      (bbls || []).forEach((b) => allowed.add(String(b)));
      // Drop any selected entries no longer allowed
      let changed = false;
      selected.forEach((b) => {
        if (!allowed.has(b)) { selected.delete(b); changed = true; }
      });
      if (changed) notify();
    },
    isAllowed(bbl) { return allowed.has(String(bbl)); },

    /* ----- selection accessors ----- */
    getSelected() { return Array.from(selected); },
    isSelected(bbl) { return selected.has(String(bbl)); },

    /* ----- mutations ----- */
    select(bbl) {
      const key = String(bbl);
      if (!allowed.has(key) || selected.has(key)) return false;
      selected.add(key);
      notify();
      return true;
    },
    deselect(bbl) {
      const key = String(bbl);
      if (!selected.delete(key)) return false;
      notify();
      return true;
    },
    toggle(bbl) {
      const key = String(bbl);
      if (!allowed.has(key)) return false;
      if (selected.has(key)) {
        selected.delete(key);
      } else {
        selected.add(key);
      }
      notify();
      return true;
    },
    /** Replace the selection set wholesale. */
    setSelected(bbls) {
      const next = new Set((bbls || []).map(String).filter((b) => allowed.has(b)));
      if (next.size === selected.size && Array.from(next).every((b) => selected.has(b))) return false;
      selected.clear();
      next.forEach((b) => selected.add(b));
      notify();
      return true;
    },

    /* ----- subscribe ----- */
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

/**
 * Get-or-create the store attached to the block-analyzer container that
 * contains the given DOM node. Returns null if no container is found.
 */
export function getStoreFor(node) {
  const container = findContainer(node);
  if (!container) return null;
  if (!container[STORE_PROP]) {
    container[STORE_PROP] = createStore();
  }
  return container[STORE_PROP];
}

const api = { getStoreFor };

if (typeof window !== 'undefined') {
  window.PsBlockAnalyzerSelection = api;
}

export default api;
